<template>
  <el-dialog
    :model-value="modelValue"
    custom-class="el-dialog--custom"
    width="700px"
    top="0"
    :title="$t('saved_search_conditions')"
    :close-on-click-modal="true"
    :destroy-on-close="true"
    :show-close="true"
    @close="$emit('update:modelValue', false)"
  >
    <div v-if="savedSearchConditions.length > 0">
      <div
        v-for="condition in savedSearchConditions"
        :key="condition.id"
        class="group flex items-center justify-between border-b border-gray-200 py-3 px-2 rounded-md transition-all duration-150 cursor-pointer"
        :class="
          condition.id === matchedConditionId
            ? 'bg-gray-300 border-yellow-300 shadow-sm'
            : 'hover:bg-blue-50 hover:shadow-sm hover:border-blue-100'
        "
        @click="applyCondition(condition)"
      >
        <div class="flex items-center flex-1 mr-4 min-w-0">
          <div class="flex flex-col min-w-0">
            <span
              class="font-semibold transition-colors truncate"
              :class="condition.id === matchedConditionId ? 'text-blue-700' : 'text-gray-800 group-hover:text-blue-700'"
            >
              {{ condition.name }}
            </span>
          </div>
        </div>
        <div class="flex items-center gap-2 flex-shrink-0" @click.stop>
          <el-button type="primary" size="medium" @click="openRenameModal(condition)">
            {{ $t('change_name') }}
          </el-button>
          <el-button
            type="default"
            class="bg-red-min text-white focus:text-white"
            size="medium"
            @click="openDeleteModal(condition.id)"
          >
            {{ $t('delete') }}
          </el-button>
        </div>
      </div>
    </div>
    <div v-else class="text-center py-8 text-gray-500">
      {{ $t('empty') }}
    </div>
  </el-dialog>

  <DeleteConfirmDialog
    v-model="isShowDeleteModal"
    :message="$t('confirm_delete_saved_search_condition')"
    :loading="isDeleting"
    @confirm="confirmDelete"
  />

  <el-dialog
    v-model="isShowRenameModal"
    custom-class="el-dialog--custom"
    width="400px"
    top="0"
    :title="$t('change_name')"
    :close-on-click-modal="false"
    :destroy-on-close="true"
    :show-close="true"
    @close="isShowRenameModal = false"
  >
    <el-input
      :disabled="isRenaming"
      v-model="renamingConditionName"
      :placeholder="$t('change_name')"
      @keyup.enter="confirmRename"
    />
    <p v-if="isRenameDuplicate" class="text-red-500 text-xs mt-1">{{ $t('duplicate_saved_search_condition_name') }}</p>
    <template #footer>
      <div class="flex">
        <div class="flex-1">
          <el-button type="default" class="btn-default-cancel" @click="isShowRenameModal = false">
            {{ $t('barcode_type.cancel') }}
          </el-button>
        </div>
        <div class="flex-1">
          <el-button type="primary" :disabled="isRenameDisabled" :loading="isRenaming" @click="confirmRename">
            {{ $t('confirm') }}
          </el-button>
        </div>
      </div>
    </template>
  </el-dialog>
</template>

<script lang="ts">
import DeleteConfirmDialog from '@/components/common/DeleteConfirmDialog.vue'
import { DELETE_SAVED_SEARCH_CONDITION, RENAME_SAVED_SEARCH_CONDITION } from '@/store/actions'
import errorHandler from '@/utils/errorHandler'
import { ISavedSearchCondition } from '@/utils/types'
import { openMessage } from '@/utils/utils'
import { Options, Vue } from 'vue-class-component'
import { Prop } from 'vue-property-decorator'

@Options({
  name: 'SavedSearchConditionDialog',
  components: { DeleteConfirmDialog },
  emits: ['update:modelValue', 'apply'],
})
export default class SavedSearchConditionDialog extends Vue {
  @Prop({ type: Boolean, default: false }) readonly modelValue!: boolean
  @Prop({ type: Array, default: () => [] }) readonly savedSearchConditions!: ISavedSearchCondition[]
  @Prop({ type: String, default: null }) readonly matchedConditionId!: string | null

  isShowRenameModal = false
  isShowDeleteModal = false
  isRenaming = false
  isDeleting = false
  renamingConditionId: string | null = null
  renamingConditionName = ''
  deletingConditionId: string | null = null

  get isRenameDuplicate(): boolean {
    const trimmed = this.renamingConditionName.trim().toLowerCase()
    return (
      !!trimmed &&
      this.savedSearchConditions.some(
        (s) => s.id !== this.renamingConditionId && s.name.trim().toLowerCase() === trimmed
      )
    )
  }

  get isRenameDisabled(): boolean {
    return !this.renamingConditionName.trim() || !this.renamingConditionId || this.isRenameDuplicate || this.isRenaming
  }

  applyCondition(condition: ISavedSearchCondition) {
    this.$emit('apply', condition)
  }

  openRenameModal(condition: ISavedSearchCondition) {
    this.renamingConditionId = condition.id
    this.renamingConditionName = condition.name
    this.isShowRenameModal = true
  }

  async confirmRename() {
    if (this.isRenameDisabled) return
    this.isRenaming = true
    try {
      await this.$store.dispatch(RENAME_SAVED_SEARCH_CONDITION, {
        id: this.renamingConditionId,
        name: this.renamingConditionName.trim(),
      })
      openMessage(this.$t('rename_saved_search_condition_success'), 'success')
    } catch (e) {
      errorHandler(e)
    } finally {
      this.isRenaming = false
      this.isShowRenameModal = false
      this.renamingConditionId = null
      this.renamingConditionName = ''
    }
  }

  openDeleteModal(id: string) {
    this.deletingConditionId = id
    this.isShowDeleteModal = true
  }

  async confirmDelete() {
    if (!this.deletingConditionId) return
    this.isDeleting = true
    try {
      await this.$store.dispatch(DELETE_SAVED_SEARCH_CONDITION, this.deletingConditionId)
      openMessage(this.$t('delete_saved_search_condition_success'), 'success')
    } catch (e) {
      errorHandler(e)
    } finally {
      this.isDeleting = false
      this.isShowDeleteModal = false
      this.deletingConditionId = null
    }
  }
}
</script>
